import { type ReactNode } from 'react';
import {
  CharlieAvatar,
  CharlieAvatarFallback,
  CharlieAvatarImage,
  cn,
} from '@ver.id/libs.ui.charlie';

type Props = {
  src?: string;
  name: string;
  /** Size, radius and surface of the tile. Defaults to a 40px rounded square. */
  className?: string;
  /** Marker overlapping the bottom-right corner, such as a status badge. */
  badge?: ReactNode;
};

/**
 * AppLogo — An identity app's or issuer's logo, with a monogram of its name
 * when there is no image or the image fails to load.
 */
export default function AppLogo({ src, name, className, badge }: Props) {
  const logo = (
    <CharlieAvatar className={cn('size-10 rounded-lg', className)}>
      {src && (
        <CharlieAvatarImage src={src} alt={name} className="object-contain" />
      )}
      <CharlieAvatarFallback
        delayMs={src ? 300 : undefined}
        className="rounded-none text-xs font-semibold text-muted-foreground"
      >
        {monogram(name)}
      </CharlieAvatarFallback>
    </CharlieAvatar>
  );

  if (!badge) return logo;

  return (
    <span className="relative flex w-fit shrink-0">
      {logo}
      <span className="absolute -bottom-1 -right-1">{badge}</span>
    </span>
  );
}

function monogram(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
}
